import { useSelector, shallowEqual } from "react-redux";
import { get, pick } from "lodash";
const generateObjectDependencies = (object, nameOrPath) => {
  let valuesSelector = {};
  nameOrPath.map(
    ({ path, values }) => Array.isArray(values) && values.forEach((value) => {
      valuesSelector = {
        ...valuesSelector,
        [value]: path ? object[path]?.[value] : object[value] ?? null
      };
    })
  );
  return valuesSelector;
};
const loanDataFieldSelector = (nameOrPath, defaultValue = null) => ({ EPPS: { loanInfo = {} } }) => {
  if (typeof nameOrPath === "string")
    return { value: get(loanInfo, nameOrPath) ?? defaultValue };
  if (Array.isArray(nameOrPath))
    return generateObjectDependencies(loanInfo, nameOrPath);
  return nameOrPath ? null : loanInfo;
};
const isShallowEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};
const useLoanDataFieldSelector = (pathValue, defaultValue) => useSelector(loanDataFieldSelector(pathValue, defaultValue), isShallowEqual);
const getDefaultFocusedDate = () => {
  const parsedCurrentDateValue = (/* @__PURE__ */ new Date()).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
  return parsedCurrentDateValue;
};
const formatTime = (date) => {
  const parsedDate = new Date(date);
  if (date) {
    let hours = parsedDate.getHours();
    let minutes = parsedDate.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours %= 12;
    if (hours === 0) {
      hours = 12;
    }
    if (hours < 10) {
      hours = `0${hours}`;
    }
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    return `${hours}:${minutes} ${ampm}`;
  }
  return "";
};
const formatDate = (date) => {
  if (date) {
    const parsedDate = new Date(date);
    const year = parsedDate.getFullYear();
    const month = (1 + parsedDate.getMonth()).toString().padStart(2, "0");
    const day = parsedDate.getDate().toString().padStart(2, "0");
    return `${month}/${day}/${year}`;
  }
  return "";
};
const formatDateTime = (date) => {
  const formatedDate = formatDate(date);
  const formatedTime = formatTime(date);
  if (formatedDate !== "" && formatedTime !== "")
    return `${formatedDate} ${formatedTime}`;
  return "";
};
const setIsRefinance = (schemaForm, value) => {
  let isRefinanceType = false;
  const refinanceTypes = [2, 3];
  const newLoanPurpose = parseInt(value?.value, 10);
  if (newLoanPurpose) {
    isRefinanceType = refinanceTypes.includes(newLoanPurpose);
  }
  schemaForm.setValue("formValidations.isRefinance", isRefinanceType);
};
const setIsGovie = (schemaForm, value) => {
  let isGovie = false;
  const govies = [2, 3];
  if (Array.isArray(value)) {
    isGovie = value.some((elem) => govies.includes(parseInt(elem?.value, 10)));
  }
  schemaForm.setValue("formValidations.isGovie", isGovie);
};
const setIsVA = (schemaForm, value) => {
  let isVA = false;
  const VA = [3];
  if (Array.isArray(value)) {
    isVA = value.some((elem) => VA.includes(parseInt(elem?.value, 10)));
  }
  schemaForm.setValue("formValidations.isVA", isVA);
};
const setIsIRRRL = (schemaForm, value) => {
  let isIRRRL = false;
  const IRRRL = [55];
  if (Array.isArray(value)) {
    isIRRRL = value.some((elem) => IRRRL.includes(parseInt(elem?.value, 10)));
  }
  schemaForm.setValue("formValidations.isIRRRL", isIRRRL);
};
const setIsHELOC = (schemaForm, value, lookupsLoanTerms) => {
  let isHELOC = false;
  if (Array.isArray(value) && Array.isArray(lookupsLoanTerms)) {
    const loanTerms = value.map(
      (option) => !isNaN(Number(option.value)) ? parseInt(option.value, 10) : option.value
    );
    const loanIsHELOC = lookupsLoanTerms.filter((elem) => loanTerms.includes(elem.value)).some((elem) => elem.heloc);
    isHELOC = loanIsHELOC;
  }
  schemaForm.setValue("formValidations.isHELOC", isHELOC);
};
const selectLookups = (nameOrPath, defaultValue = null) => (state) => {
  const data = state?.eppsData?.lookups?.data || {};
  if (Array.isArray(nameOrPath)) {
    return pick(data, nameOrPath);
  }
  if (typeof nameOrPath === "string") {
    return get(data, nameOrPath, defaultValue);
  }
  return data;
};
const useLookup = (lookup, defaultValue) => useSelector(selectLookups(lookup, defaultValue), shallowEqual);
const handleTotalAmountValue = (schemaForm, value) => {
  const amounts = schemaForm.getValues([
    "loanInformation.firstMortgageAmount",
    "loanInformation.financedAmount",
    "loanInformation.feeAmountFinanced"
  ]);
  const totalValue = calculateTotalLoanAmount(
    amounts[0],
    amounts[1],
    amounts[2]
  );
  return totalValue;
};
const parseCompensationValue = (value) => {
  let getValue = null;
  if (value) {
    if (value === "Borrower-Paid") {
      getValue = 1;
    } else {
      getValue = 2;
    }
  }
  return getValue;
};
const safeSum = (...numbers) => {
  let res = 0;
  numbers.forEach((num) => {
    res += Number(num || 0);
  });
  return res;
};
const calculateTotalLoanAmount = (firstMortgageAmount, financedAmount, govtFeeFinancedAmount) => {
  let result = 0;
  const ParsedLoanAmount = parseInt(firstMortgageAmount, 10) || 0;
  const ParsedFinancedAmount = parseInt(financedAmount, 10) || 0;
  const ParsedGovtFeeFinancedAmount = parseFloat(govtFeeFinancedAmount) || 0;
  result = safeSum(
    ParsedLoanAmount,
    Math.floor(ParsedFinancedAmount),
    ParsedGovtFeeFinancedAmount
  );
  return result;
};
export {
  calculateTotalLoanAmount,
  formatDate,
  formatDateTime,
  formatTime,
  generateObjectDependencies,
  getDefaultFocusedDate,
  handleTotalAmountValue,
  isShallowEqual,
  loanDataFieldSelector,
  parseCompensationValue,
  safeSum,
  selectLookups,
  setIsGovie,
  setIsHELOC,
  setIsIRRRL,
  setIsRefinance,
  setIsVA,
  useLoanDataFieldSelector,
  useLookup
};
